'use client';

/**
 * ReviewStrip — the "What students say" review strip on /super-fingers
 * (light, cream section) and /store (dark band). Replaces Senja's card
 * marquee 375ae9ff (Senja sunset, 2026-09-26).
 *
 * Cards: Aria §3 + Round 2 + Round 3 (reviews.css). Round 5 (Lars): cards are
 * bigger and taller, the text is cut off only past the card's line budget, and
 * "Read more" appears only on a card whose review really runs past it.
 * Round 3 (Lars, 2026-09-26): the WHOLE card opens the full review in
 * ReviewModal (pointer cursor; one keyboard stop per card). A drag never counts as a click (the marquee
 * swallows the click that ends a drag). A video card never plays in the strip:
 * it shows the poster, the name and the kit play button, and a click opens
 * the same pop-up, where the video plays in a large player.
 * Round 7 (Lars, 2026-09-27): every card has the pop-up's header — avatar left;
 * name, stars and student type stacked on its right. Cards are wider and
 * shorter, titles wrap in full (never cut off), and the row eases to a stop on
 * hover instead of stopping dead (ReviewMarquee), cruising at 40px/s.
 * The order is set on the server: the same person is never side by side
 * (lib/review-feed.ts spreadByPerson).
 */
import { useLayoutEffect, useRef, useState } from 'react';
import type { ReviewItem } from '@/lib/review-feed';
import { Segments } from '@/app/wall-of-love/_sections/WolParts';
import PfPlayButton from '@/components/video/PfPlayButton';
import ReviewMarquee, { useIsMarqueeClone } from './ReviewMarquee';
import ReviewModal, { ReviewAvatar, ReviewStars } from './ReviewModal';
import { testimonialPosterSrc } from './cdn';
import './reviews.css';

function TextCard({ item, onOpen }: { item: ReviewItem; onOpen: (i: ReviewItem) => void }) {
  const clone = useIsMarqueeClone();
  const textRef = useRef<HTMLParagraphElement>(null);
  const [overflows, setOverflows] = useState(false);
  // Round 5 (Lars: "Only very long ones you put 'Read more'"): the text uses
  // the card's whole height (Aria's pinned card size) — the cut-off is the card
  // itself, in whole lines, with room kept for the label — and "Read more"
  // shows only when THIS review really runs past it (scrollHeight >
  // clientHeight). Re-measured when the card resizes or the web fonts arrive.
  useLayoutEffect(() => {
    const el = textRef.current;
    const card = el?.closest<HTMLElement>('.rv-card');
    if (!el || !card) return;
    const measure = () => {
      const lh = parseFloat(getComputedStyle(el).lineHeight);
      const more = card.querySelector<HTMLElement>('.rv-card__more');
      const reserve = (more ? parseFloat(getComputedStyle(more).lineHeight) : 24) + 8; // label line + --space-8
      const room = card.getBoundingClientRect().bottom - parseFloat(getComputedStyle(card).paddingBottom) - reserve - el.getBoundingClientRect().top;
      if (lh > 0) el.style.setProperty('--rv-cut-lines', String(Math.max(3, Math.floor(room / lh))));
      setOverflows(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(card);
    let live = true;
    document.fonts?.ready.then(() => live && measure());
    return () => {
      live = false;
      ro.disconnect();
    };
  }, [item.id]);
  return (
    // The whole card opens the review (a click anywhere on it). Its keyboard
    // stop is one button: the visible "Read more" when the review is cut off,
    // otherwise a visually hidden one; the card shows the focus ring.
    <article
      className="rv-card rv-card--open"
      data-review-id={item.id}
      data-person={item.personKey}
      data-overflow={overflows ? 'true' : 'false'}
      onClick={() => onOpen(item)}
    >
      <div className="rv-card__head">
        <ReviewAvatar item={item} className="rv-card__avatar" />
        <div className="rv-card__who">
          <p className="rv-card__name">{item.name}</p>
          <ReviewStars rating={item.rating} />
          {item.studentType ? <p className="rv-card__type">{item.studentType}</p> : null}
        </div>
      </div>
      {item.title ? <p className="rv-card__title">{item.title}</p> : null}
      <p ref={textRef} className="rv-card__text">
        <Segments segments={item.segments} fallback={item.plain} />
      </p>
      <button
        type="button"
        className={overflows ? 'rv-card__more' : 'rv-card__more rv-card__more--sr'}
        tabIndex={clone ? -1 : undefined}
        aria-label={`Read ${item.name}'s full review`}
      >
        Read more
      </button>
    </article>
  );
}

function VideoCard({ item, onOpen }: { item: ReviewItem; onOpen: (i: ReviewItem) => void }) {
  const clone = useIsMarqueeClone();
  if (!item.video) return null;
  const first = item.name.replace(/^@/, '').split(/\s+/)[0] || item.name;
  const poster = testimonialPosterSrc(item.video);
  return (
    // The whole card opens the pop-up; the kit play button is its keyboard stop.
    <article className="rv-card rv-card--video rv-card--open" data-review-id={item.id} data-person={item.personKey} onClick={() => onOpen(item)}>
      <div className="rv-video">
        <div className="rv-video__stage">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img className="rv-video__poster" src={poster} alt="" loading="lazy" decoding="async" draggable={false} />
          <div className="rv-video__overlay" aria-hidden="true">
            {item.rating ? <ReviewStars rating={item.rating} /> : null}
            <div className="rv-video__who">
              <span className="rv-video__bar" />
              <p className="rv-video__name">{item.name}</p>
              {item.studentType ? <p className="rv-video__type">{item.studentType}</p> : null}
            </div>
          </div>
          <PfPlayButton
            className="rv-card__play"
            label={`Play the video from ${first}`}
            tabIndex={clone ? -1 : undefined}
          />
        </div>
      </div>
    </article>
  );
}

export default function ReviewStrip({
  items,
  variant,
  label,
}: {
  items: ReviewItem[];
  variant: 'light' | 'dark';
  label: string;
}) {
  const [open, setOpen] = useState<ReviewItem | null>(null);
  if (items.length === 0) return null;
  return (
    <div className={`rv-strip rv-strip--${variant}`} data-testid="review-strip">
      {/* Round 7 (Lars: "slide them a little bit faster. Not too fast"): 40px/s
          (was 26.5). Hover eases to a stop over 1500ms; leave eases back in
          250ms later, over 1000ms (ReviewMarquee). */}
      <ReviewMarquee label={label} speed={40} resumeDelayMs={250} stopMs={1500} startMs={1000}>
        {items.map((it) =>
          it.kind === 'video' ? <VideoCard key={it.id} item={it} onOpen={setOpen} /> : <TextCard key={it.id} item={it} onOpen={setOpen} />
        )}
      </ReviewMarquee>
      <ReviewModal item={open} onClose={() => setOpen(null)} />
    </div>
  );
}
